import { useCallback, useMemo, useState } from 'react';

import { BillingGroup, GUID } from '@ndvr/billing-api';

import { useAccountLookup } from 'views/portfolio/account-queries';

import { useActivateBillingGroups, useDeactivateBillingGroups } from './billing-group-queries';
import { resolveAccountName } from './utils';

export const useBillingGroupAccountNameMap = (billingGroups: BillingGroup[]) => {
    const accountGUIDs = useMemo(
        () => [
            ...new Set(
                billingGroups.flatMap((group) =>
                    group.accounts.flatMap((account) =>
                        account.settlementAccountGUIDOverride
                            ? [account.accountGUID, account.settlementAccountGUIDOverride]
                            : [account.accountGUID],
                    ),
                ),
            ),
        ],
        [billingGroups],
    );
    const { data: accountLookups = [], isLoading: namesLoading } = useAccountLookup(accountGUIDs);
    const nameByGUID = useMemo(
        () => new Map(accountLookups.map((account) => [account.accountGUID, resolveAccountName(account)])),
        [accountLookups],
    );
    return { nameByGUID, namesLoading };
};

interface BillingGroupActionsOptions {
    // Runs after a successful activate/deactivate — e.g. to clear a bulk selection.
    onActionSuccess?: () => void;
}

// Non-visual glue shared by the admin BillingGroupsPage and the household Billing Groups
// tab: the create/edit dialog state and the activate/deactivate mutations + confirm flow.
// Each view renders its own UI around this.
export const useBillingGroupActions = ({ onActionSuccess }: BillingGroupActionsOptions = {}) => {
    const activateMutation = useActivateBillingGroups();
    const deactivateMutation = useDeactivateBillingGroups();

    const [editingItem, setEditingItem] = useState<BillingGroup | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deactivatingGUIDs, setDeactivatingGUIDs] = useState<GUID<BillingGroup>[] | null>(null);

    const openCreate = useCallback(() => {
        setEditingItem(null);
        setDialogOpen(true);
    }, []);

    const openEdit = useCallback((billingGroup: BillingGroup) => {
        setEditingItem(billingGroup);
        setDialogOpen(true);
    }, []);

    const activate = useCallback(
        (billingGroupGUIDs: GUID<BillingGroup>[]) =>
            activateMutation.mutate(billingGroupGUIDs, { onSuccess: onActionSuccess }),
        [activateMutation, onActionSuccess],
    );

    const requestDeactivate = useCallback((billingGroupGUIDs: GUID<BillingGroup>[]) => {
        setDeactivatingGUIDs(billingGroupGUIDs);
    }, []);

    const cancelDeactivate = useCallback(() => setDeactivatingGUIDs(null), []);

    const confirmDeactivate = useCallback(() => {
        if (!deactivatingGUIDs) return;
        deactivateMutation.mutate(deactivatingGUIDs, {
            onSuccess: () => {
                setDeactivatingGUIDs(null);
                onActionSuccess?.();
            },
        });
    }, [deactivatingGUIDs, deactivateMutation, onActionSuccess]);

    return {
        activateMutation,
        deactivateMutation,
        editingItem,
        dialogOpen,
        setDialogOpen,
        openCreate,
        openEdit,
        activate,
        deactivatingGUIDs,
        requestDeactivate,
        cancelDeactivate,
        confirmDeactivate,
    };
};
